import Box from '@mui/material/Box'
import {
  DoorsIcon,
  ElectricalIcon,
  FoundationIcon,
  FramingIcon,
  ListShowMoreIcon,
  PlumbingIcon,
  StairsIcon,
  VentilationIcon,
  WallsIcon,
  WindowsIcon,
} from '../../../Svgs'
import { cloneElement, useCallback, useState } from 'react'
import { Collapse, Divider, Grid, IconButton, Typography } from '@mui/material'
import { getEnums } from '../../../../utils/Common'
import {
  BuildingEditingTool,
  BuildingObjectTool,
  BuildingViewMode,
} from '../../../../models/Construction'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import { setSelectedTool } from '../../../../actions/construction'
import CreateWall from './CreateWall'
import CreateWindow from './CreateWindow'
import CreateDoor from './CreateDoor'
import EditElement from './EditElement'
import FloorHeight from './FloorHeight'

interface ObjectToolsProps {
  isDisabled?: boolean
}

const ObjectToolsIcons: Record<BuildingObjectTool, JSX.Element> = {
  [BuildingObjectTool.Walls]: <WallsIcon />,
  [BuildingObjectTool.Doors]: <DoorsIcon />,
  [BuildingObjectTool.Windows]: <WindowsIcon />,
  [BuildingObjectTool.Framing]: <FramingIcon />,
  [BuildingObjectTool.Stairs]: <StairsIcon />,
  [BuildingObjectTool.Ventilation]: <VentilationIcon />,
  [BuildingObjectTool.Electrical]: <ElectricalIcon />,
  [BuildingObjectTool.Plumbing]: <PlumbingIcon />,
  [BuildingObjectTool.Foundation]: <FoundationIcon />,
}

const ObjectTools: React.FC<ObjectToolsProps> = ({ isDisabled }) => {
  const dispatch = useDispatch()
  const selectedTool = useSelector(
    (state: RootState) => state.construction.selectedTool,
  )
  const selectedBuildingMode = useSelector(
    (state: RootState) => state.construction.selectedBuildingMode,
  )

  const [isOpen, setIsOpen] = useState(true)

  const renderSelectedTool = useCallback(() => {
    if (!selectedTool) {
      return null
    }

    switch (selectedTool) {
      case BuildingObjectTool.Walls:
        return <CreateWall />
      case BuildingObjectTool.Windows:
        return <CreateWindow />
      case BuildingObjectTool.Doors:
        return <CreateDoor />
      case BuildingEditingTool.Marquee:
      case BuildingEditingTool.DirectSelect:
        return <EditElement />
      default:
        return null
    }
  }, [selectedTool])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Object
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <Box
          sx={{
            flexGrow: 1,
            padding: '8px 12px',
          }}
        >
          <Grid container columns={10} columnSpacing='4px' rowSpacing='8px'>
            {getEnums(BuildingObjectTool).map(([key, value]) => {
              const isSelected = selectedTool === value
              return (
                <Grid key={key} size={2}>
                  <IconButton
                    disabled={isDisabled}
                    onClick={() => dispatch(setSelectedTool(value))}
                    sx={{
                      width: '100%',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#1705E5' : '#EFEFEF',
                      '&:hover': {
                        backgroundColor: isSelected ? '#1505CE' : '#E2E2E2',
                      },
                    }}
                  >
                    {cloneElement(ObjectToolsIcons[value], {
                      sx: {
                        color: isSelected ? 'white' : '#04002E',
                        fontSize: '24px',
                      },
                    })}
                  </IconButton>
                </Grid>
              )
            })}
          </Grid>
        </Box>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>

      {selectedBuildingMode === BuildingViewMode.SingleStorey && (
        <FloorHeight />
      )}

      {renderSelectedTool()}
    </Box>
  )
}

export default ObjectTools
